import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Status } from 'src/constants/Interface';
import 'dayjs/locale/ja';
dayjs.locale('ja');
dayjs().format();
dayjs.extend(isSameOrBefore);

export type ValidationResponse = {
  status: Status.INVALID;
  errors: ValidationManager['errors'];
};

/**
 *
 * バリデーションの為に使用する。
 * 共通化しにくい各スキーマごとのバリデーションがあれば子クラスを作成すること
 *
 * @example
 * const name = 'hoge';
 * const val = new ValidationManager();
 *
 * //- validation
 * val.isString('name', name)
 * val.validateMaxlength('name', name, 140)
 *
 * //- check
 * const isPass = val.isPass();
 */

interface Props {
  errors: {
    [key: string]: string[];
  };
  regexp: { [k in any]: string };
  messages: { [k in any]: string };
}

class ValidationManager {
  errors: Props['errors'];
  regexp: Props['regexp'];
  messages: Props['messages'];

  constructor() {
    this.errors = {};
    /* eslint-disable */
    this.regexp = {
      kana: '^[ァ-ンヴー　 ]*$',
      isDigit: '^[0-9]',
      url: "^(https?)(://[-_.!~*'()a-zA-Z0-9;/?:@&=+$,%#]+)$",
      phone: '^0[0-9]{9,10}$',
      halfAlphaNumeric: '^[0-9a-zA-Z]*$',
      address: "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$",
    };
    /* eslint-enable */
    this.messages = {
      isKana: 'カタカナで入力してください',
      isString: '文字列で入力してください',
      isDigit: '数字で入力してください',
      isGtZero: '0より大きい数値を入力してください',
      isPhone: '電話番号は、9桁もしくは10桁の数字を入力してください',
      isBoolean: 'ブール値で入力してください',
      isRequired: '必須項目です',
      isUrl: 'URLの形式で入力してください',
      isHalfAlphaNumeric: '半角英数字で入力してください',
      isPostalCode: '郵便番号は7桁で設定してください',
      isAddress: 'メールアドレスの形式で入力してください',
      isArrayLengthGtZero: '必須項目です',
    };
  }

  setRegExp = (key: string, val: string) => {
    this.regexp[key] = val;
  };

  setMessages = (key: string, val: string) => {
    this.messages[key] = val;
  };

  isPass() {
    const errors = this.getErrors();
    console.log('Validation : isPass?', errors);
    return Object.keys(errors).length === 0;
  }

  addErrors(key: string, message: string) {
    const exists = Object.keys(this.errors).find((v) => v === key);
    if (exists) {
      this.errors[key].push(message);
    } else {
      this.errors = { ...this.errors, [key]: [message] };
    }
  }

  getErrors() {
    return this.errors;
  }

  regexpTest(reg: string, str: any) {
    const regexp = RegExp(reg);
    return regexp.test(str);
  }

  isKana(key: string, kana: string) {
    const is = this.regexpTest(this.regexp['kana'], kana);
    if (!is) this.addErrors(key, this.messages['isKana']);
  }

  isDigit(key: string, s: any) {
    const is = this.regexpTest(this.regexp['isDigit'], s);
    if (!is) this.addErrors(key, this.messages['isDigit']);
  }

  isGtZero(key: string, s: any) {
    const is = typeof s === 'number' && s > 0;
    if (!is) this.addErrors(key, this.messages['isGtZero']);
  }

  isString(key: string, s: any) {
    const is = typeof s === 'string';
    if (!is) this.addErrors(key, this.messages['isString']);
  }

  isArrayLengthGtZero(key: string, s: any) {
    const is = Array.isArray(s) && s.length > 0;
    if (!is) this.addErrors(key, this.messages['isArrayLengthGtZero']);
  }

  isBoolean(key: string, v: any) {
    const is = typeof v === 'boolean';
    if (!is) this.addErrors(key, this.messages['isBoolean']);
  }

  isRequired(key: string, v: any) {
    const is = v !== null && v !== undefined && v !== '' && v !== 0;
    if (!is) this.addErrors(key, this.messages['isRequired']);
  }

  isPhoneNumber(key: string, number: string) {
    const is = this.regexpTest(this.regexp['phone'], number);
    if (!is) this.addErrors(key, this.messages['isPhone']);
  }

  isUrl(key: string, url: string) {
    const is = this.regexpTest(this.regexp['url'], url);
    if (!is) this.addErrors(key, this.messages['isUrl']);
  }

  isAddress(key: string, address: string) {
    const is = this.regexpTest(this.regexp['address'], address);
    if (!is) this.addErrors(key, this.messages['isAddress']);
  }

  validateMinLength(key: string, s: string, min: number) {
    const l = s.length;
    if (l < min) this.addErrors(key, `最低でも、${min}文字入力してください`);
  }

  validateMaxLength(key: string, s: string, max: number) {
    const l = s.length;
    if (l > max) this.addErrors(key, `文字数をオーバーしています。${max}文字までにしてください`);
  }

  isNowAfter(date: any) {
    const now = dayjs();
    const isOver = now.isAfter(date);
    return isOver;
  }

  isNowBefore(date: any) {
    const now = dayjs();
    const isOver = now.isBefore(date);
    return isOver;
  }

  isValidOpenTime(open_time: string, close_time: string) {
    const today = dayjs().format('YYYY-MM-DD');
    return dayjs(`${today} ${open_time}`).isSameOrBefore(`${today} ${close_time}`);
  }

  throwValidateError(errors: ValidationManager['errors']): ValidationResponse {
    return {
      status: Status.INVALID,
      errors,
    };
  }
}

export default ValidationManager;
